import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/auth.css';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { resetPassword, currentUser, error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // If user is already logged in, redirect to profile
  useEffect(() => {
    if (currentUser) {
      navigate('/profile');
    }
  }, [currentUser, navigate]);

  // Update local error state when auth error changes
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setIsLoading(true);

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err) {
      setError('Failed to send password reset email. Please try again.');
      console.error('Password reset error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Reset Your Password</h1>
            <p>Enter your email address to receive a password reset link</p>
          </div>

          {error && (
            <div className="auth-error">
              <i className="fas fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {success ? (
            <div className="auth-success">
              <i className="fas fa-check-circle"></i>
              <div className="success-message">
                <h3>Check your email</h3>
                <p>We've sent a password reset link to <strong>{email}</strong></p>
                <p>Please check your inbox and follow the instructions to reset your password.</p>
                <Link to="/login" className="auth-submit-btn">
                  Return to Login
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="form-control"
                  required
                />
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Sending...</span>
                  </>
                ) : (
                  <span>Send Reset Link</span>
                )}
              </button>
            </form>
          )}

          <div className="auth-footer">
            <p>
              Remember your password?{' '}
              <Link to="/login" className="auth-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        <div className="auth-info">
          <div className="auth-info-content">
            <h2>DataHarbor</h2>
            <p>Decentralized marketplace for AI datasets and model weights</p>
            <ul className="auth-features">
              <li>
                <i className="fas fa-database"></i>
                <span>Access high-quality AI datasets</span>
              </li>
              <li>
                <i className="fas fa-link"></i>
                <span>Transparent on-chain tracking</span>
              </li>
              <li>
                <i className="fas fa-hand-holding-usd"></i>
                <span>Fair revenue sharing for creators</span>
              </li>
              <li>
                <i className="fas fa-shield-alt"></i>
                <span>Secure and decentralized storage</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
